import AchievementIcon from '@/components/Icon/AchievementIcon';
import LabelBackground from '@/components/LabelBackground/LabelBackground';
import LargeSquareBackground from '@/components/LargeSquareBackground/LargeSquareBackground';
import SectionBackground from '@/components/SectionBackground/SectionBackground';
import { CalculatedAchievement } from '@/hooks/useAchievements';
import cn from '@/lib/cs';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { useMemo, useRef } from 'react';
import CollectionCard from '../Collections/CollectionCard/CollectionCard';
import { useActiveTab } from '../Tabs/useActiveTab';

const AchievementListItem = ({
	achievement,
	index,
	headingElement,
	isInsideDialog,
	isOnPageOfSpeciesId,
}: {
	achievement: CalculatedAchievement;
	index: number;
	headingElement: 'h2' | 'h3';
	isInsideDialog: boolean;
	isOnPageOfSpeciesId?: number;
}) => {
	const activeTab = useActiveTab();
	const reduceMotion = useReducedMotion();
	const selfRef = useRef<HTMLLIElement>(null);
	const isInView = useInView(selfRef, {
		once: true,
	});

	const HeadingElement = headingElement;

	const connectedSpecies = useMemo(
		() =>
			// Put the species with the highest score first
			// and if a isOnPageOfSpeciesId is provided, put that species first
			achievement.calculatedScorePerSpecies
				.sort((a, b) => b.score - a.score)
				.sort((a, b) => {
					if (a.speciesId === isOnPageOfSpeciesId) return -1;
					if (b.speciesId === isOnPageOfSpeciesId) return 1;
					return 0;
				}),
		[achievement.calculatedScorePerSpecies],
	);

	return (
		<motion.li
			ref={selfRef}
			className="relative min-w-0 text-accentCompat"
			initial={{
				scaleY: reduceMotion || !isInsideDialog ? 1 : 0,
			}}
			animate={
				isInView && {
					scaleY: 1,
					transition: {
						delay: 0.3 + 0.1 * index,
					},
				}
			}>
			<SectionBackground
				className={cn(
					activeTab === 'achievements' && !isInsideDialog
						? 'text-secondary'
						: 'text-accent',
				)}
			/>
			<div className="relative min-w-0">
				<div className="mx-auto flex w-full max-w-[40rem] items-center gap-2 p-4 pe-3">
					<div className="grow">
						<HeadingElement className="text-balance font-brand text-3xl first-letter:text-4xl">
							{achievement.title}
						</HeadingElement>
						<p>
							{achievement.description}
							<br />
							{achievement.calculatedExplanation}
						</p>
					</div>
					<SingleAchievementScore achievement={achievement} />
				</div>
				{!isInsideDialog && connectedSpecies.length > 0 && (
					<ul
						aria-label="Soorten"
						className="mx-auto -mb-3 flex w-full min-w-0 max-w-2xl flex-row gap-2 overflow-x-auto px-4">
						{connectedSpecies.map((species, index, array) => (
							<div
								key={species.speciesId}
								className="w-24 shrink-0">
								<CollectionCard
									speciesId={species.speciesId}
									index={index}
									target="details"
									tryUserMediaIndex={array
										.filter(
											(s) =>
												s.speciesId ===
												species.speciesId,
										)
										.indexOf(species)}
								/>
							</div>
						))}
					</ul>
				)}
			</div>
		</motion.li>
	);
};

export const SingleAchievementScore = ({
	achievement,
}: {
	achievement: CalculatedAchievement;
}) => (
	<section
		aria-label={`${achievement.calculatedTotalScore} appels ${achievement.calculatedAchievedTimes} keer verdubbeld`}
		className="relative shrink-0 p-1.5 text-white">
		<LargeSquareBackground className="text-primary" />
		<div className="relative flex flex-col items-center justify-center gap-1 font-brand">
			<span className="relative z-10 flex items-center gap-0.5 px-1 text-4xl text-primary">
				<LabelBackground className="-z-10 -scale-y-100 text-overlay" />
				{achievement.calculatedTotalScore}
				<span className="sr-only"> appels</span>
				<AchievementIcon className="size-6 shrink-0 -translate-y-0.5 lg:size-6" />
			</span>
			<span>
				<span className="text-3xl leading-3">
					{achievement.calculatedAchievedTimes}
				</span>
				<span className="inline-block -translate-y-0.5 pe-0.5 font-sans">
					x
				</span>
				<span className="text-3xl leading-3">
					{achievement.calculatedTotalScore /
						achievement.calculatedAchievedTimes}
				</span>
			</span>
		</div>
	</section>
);

export default AchievementListItem;
